import axios from 'axios';

import {
  EvaluationAuthorDataWrapper,
  EvaluationChangeValidationDatesRequest,
  EvaluationCriteria,
  EvaluationCriteriaDataItemRequest,
  EvaluationItemOption,
  EvaluationOverview,
  EvaluationReport,
  EvaluationReportAuthor,
} from '@/module/Evaluation/model/EvaluationTypes';

import { Id, PersistentFile, getData } from '@feature/utils';

export function getEvaluationItemFileDownloadUrl(evaluationId: Id, authorId: Id, orderNo: number, itemId: Id) {
  return `/api/author-evaluation/${evaluationId}/author/${authorId}/criteria/${orderNo}/item/${itemId}/file-download`;
}

export function getEvaluationChiefFileDownloadUrl(evaluationId: Id, authorId: Id) {
  return `/api/author-evaluation/${evaluationId}/author/${authorId}/download-chief-file`;
}

export function getEvaluationDepartmentSignedSummaryFileDownloadUrl(evaluationId: Id, departmentId: Id) {
  return `/api/author-evaluation/${evaluationId}/department/${departmentId}/download-summary-file`;
}

export function getEvaluationDepartmentSummaryPdfFileDownloadUrl(evaluationId: Id, departmentId: Id) {
  return `/api/author-evaluation/${evaluationId}/department/${departmentId}/summary/pdf`;
}

export function getEvaluationDepartmentSummaryXlsxFileDownloadUrl(evaluationId: Id, departmentId: Id) {
  return `/api/author-evaluation/${evaluationId}/department/${departmentId}/summary/xlsx`;
}

export function getEvaluationFacultySummaryXlsxFileDownloadUrl(evaluationId: Id, facultyId: Id) {
  return `/api/author-evaluation/${evaluationId}/faculty/${facultyId}/summary/xlsx`;
}

export function getEvaluationDisciplineAdminSignedFileDownloadUrl(evaluationId: Id, authorId: Id) {
  return `/api/author-evaluation/${evaluationId}/author/${authorId}/download-discipline-signed-file`;
}

export function getEvaluationDepartmentAdminFileDownloadUrl(evaluationId: Id, authorId: Id) {
  return `/api/author-evaluation/${evaluationId}/author/${authorId}/download-department-signed-file`;
}

export function getEvaluationCollegialFileDownloadUrl(evaluationId: Id, authorId: Id) {
  return `/api/author-evaluation/${evaluationId}/author/${authorId}/download-collegial-file`;
}

export function uploadCollegialFile(evaluationId: Id, authorId: Id, newFile: File) {
  const formData = new FormData();
  formData.append('file', newFile);
  return axios
    .put<unknown>(`/author-evaluation/${evaluationId}/author/${authorId}/upload-collegial-file`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    .then(getData);
}

export function uploadChiefFile(evaluationId: Id, authorId: Id, newFile: File) {
  const formData = new FormData();
  formData.append('file', newFile);
  return axios
    .put<unknown>(`/author-evaluation/${evaluationId}/author/${authorId}/upload-chief-file`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    .then(getData);
}

export function uploadDepartmentSummaryFile(evaluationId: Id, departmentId: Id, newFile: File) {
  const formData = new FormData();
  formData.append('file', newFile);
  return axios
    .put<unknown>(`/author-evaluation/${evaluationId}/department/${departmentId}/upload-summary-file`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    .then(getData);
}

export function getDepartmentSummaryFile(evaluationId: Id, departmentId: Id) {
  return axios
    .get<PersistentFile | null>(`/author-evaluation/${evaluationId}/department/${departmentId}/summary-file`)
    .then(response => (!response.data ? null : response.data));
}

export function uploadDisciplineAdminSignedFile(evaluationId: Id, authorId: Id, newFile: File) {
  const formData = new FormData();
  formData.append('file', newFile);
  return axios
    .put<unknown>(`/author-evaluation/${evaluationId}/author/${authorId}/upload-discipline-signed-file`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    .then(getData);
}

export function uploadDepartmentAdminSignedFile(evaluationId: Id, authorId: Id, newFile: File) {
  const formData = new FormData();
  formData.append('file', newFile);
  return axios
    .put<unknown>(`/author-evaluation/${evaluationId}/author/${authorId}/upload-department-signed-file`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    .then(getData);
}

export function getAll() {
  return axios.get<EvaluationReport[]>(`/author-evaluation`).then(getData);
}

export function get(evaluationId: Id) {
  return axios.get<EvaluationReport>(`/author-evaluation/${evaluationId}`).then(getData);
}

export function open(year: number) {
  return axios.post<unknown>(`/author-evaluation/${year}/open`).then(getData);
}

export function getCriteria(evaluationId: Id) {
  return axios.get<EvaluationCriteria[]>(`/author-evaluation/${evaluationId}/criteria`).then(getData);
}

export function start(evaluationId: Id) {
  return axios.put<unknown>(`/author-evaluation/${evaluationId}/author/start`).then(getData);
}

export function getForAuthor(evaluationId: Id, authorId: Id) {
  return axios
    .get<EvaluationReportAuthor | null>(`/author-evaluation/${evaluationId}/author/${authorId}`)
    .then(getData)
    .then(data => (!data ? null : data));
}

export function getEvaluationOverview(evaluationId: Id, authorId: Id) {
  return axios.get<EvaluationOverview>(`/author-evaluation/${evaluationId}/author/${authorId}/data`).then(getData);
}

export function getCriteriaData(evaluationId: Id, authorId: Id, orderNo: number) {
  return axios
    .get<EvaluationAuthorDataWrapper>(`/author-evaluation/${evaluationId}/author/${authorId}/criteria/${orderNo}`)
    .then(getData);
}

export function addCriteriaItem(
  evaluationId: Id,
  authorId: Id,
  orderNo: number,
  request: EvaluationCriteriaDataItemRequest,
) {
  const formData = new FormData();
  if (request.newFile != null) {
    formData.append('file', request.newFile);
  }
  formData.append('payload', new Blob([JSON.stringify(request)], { type: 'application/json' }));
  return axios
    .post<unknown>(`/author-evaluation/${evaluationId}/author/${authorId}/criteria/${orderNo}/item`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    .then(getData);
}

export function editCriteriaItem(
  evaluationId: Id,
  authorId: Id,
  orderNo: number,
  itemId: Id,
  request: EvaluationCriteriaDataItemRequest,
) {
  const formData = new FormData();
  if (request.newFile != null) {
    formData.append('file', request.newFile);
  }
  formData.append('payload', new Blob([JSON.stringify(request)], { type: 'application/json' }));
  return axios
    .put<unknown>(
      `/author-evaluation/${evaluationId}/author/${authorId}/criteria/${orderNo}/item/${itemId}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    )
    .then(getData);
}

export function recalculateCriteriaItem(evaluationId: Id, authorId: Id, orderNo: number, itemId: Id) {
  return axios
    .put<unknown>(
      `/author-evaluation/${evaluationId}/author/${authorId}/criteria/${orderNo}/item/${itemId}/recalculate`,
    )
    .then(getData);
}

export function deleteCriteriaItem(evaluationId: Id, authorId: Id, orderNo: number, itemId: Id) {
  return axios
    .delete(`/author-evaluation/${evaluationId}/author/${authorId}/criteria/${orderNo}/item/${itemId}`)
    .then(getData);
}

export function getPossibleItemOptions<T>(evaluationId: Id, authorId: Id, orderNo: number) {
  return axios
    .get<
      EvaluationItemOption<T>[]
    >(`/author-evaluation/${evaluationId}/author/${authorId}/criteria/${orderNo}/possible-options`)
    .then(getData);
}

export function editValidationDates<EvaluationReport>(
  evaluationId: Id,
  request: EvaluationChangeValidationDatesRequest,
) {
  return axios
    .put<EvaluationReport>(`/author-evaluation/${evaluationId}/change-validation-dates`, request)
    .then(getData);
}
